import _, {isObject} from 'lodash';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useDrag, useDrop} from 'react-dnd';
import {NativeTypes} from 'react-dnd-html5-backend';

import {PredicateOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {useIntersectionObserver} from '../../../../../libs/shared/ui/src/hooks/domHookHelpers';
import {
  canDropWorkspaceItemAtTarget,
  computeWorkspaceDragAndDropLinePosition,
} from './workspaceDragDropHelpers';
import {markWorkspaceDragAndDropEnd, markWorkspaceDragAndDropStart} from './workspaceDragDropPerformance';
import {
  DropLinePositionsEnum,
  isWorkspaceDragItem,
  WorkspaceDragItem,
  WorkspaceDragSourceTypesEnum,
  WorkspaceItemDropHandler,
} from './workspaceDragDropTypes';

const domNativeTypes: string[] = Object.values(NativeTypes).filter((v) => v !== NativeTypes.FRONT_ATTACHMENT);

// https://github.com/react-dnd/react-dnd/issues/2183#issuecomment-683478679
function useDragLeave(callback: () => void, isOver: boolean, didDrop: boolean) {
  const ref = useRef(isOver);

  useEffect(() => {
    if (ref.current && !isOver && !didDrop) {
      callback();
    }

    ref.current = isOver;
  }, [isOver, didDrop, callback]);
}

type DragAndDropItemWithType = {type: string};

interface WorkspaceDragAndDropOptions<T extends DragAndDropItemWithType> {
  /** Is used as a reference by IntersectionObserver in order to attach the drag/drop handlers only when the element is in view. */
  scrollerElement: HTMLElement | null;
  /** Definition of the kind of workspace item can be dragged. */
  workspaceDragItem?: WorkspaceDragItem;
  /** Called when dropping (reordering) a workspace item */
  onWorkspaceItemDrop?: WorkspaceItemDropHandler;
  shouldAlphabetizeWorkspace: boolean;
  isDragDisabled?: boolean;
  /** Used to support a non-workspace related operation (currently only used to drop conversations in P1). */
  extra?: {
    type: T['type'];
    canDrop?: PredicateOf<T>;
    onDrop?: (arg: T) => void;
  };
}

interface WorkspaceDragAndDropCollectedProps {
  isDropping: boolean;
  didDrop: boolean;
  isOver: boolean;
  isRefusingDrop: boolean;
}

export function useWorkspaceDragAndDrop<T extends DragAndDropItemWithType>(
  options: WorkspaceDragAndDropOptions<T>,
) {
  const {scrollerElement, workspaceDragItem, onWorkspaceItemDrop, isDragDisabled} = options;
  markWorkspaceDragAndDropStart();

  const [isIntersecting, observerRef] = useIntersectionObserver(scrollerElement);
  const isConnected = useRef<boolean>(false);
  const dragDropSourceRef = useRef<HTMLDivElement | null>(null);
  const dropLinePreviewRef = useRef<HTMLDivElement | null>(null);
  const dropLinePosition = useRef<DropLinePositionsEnum>(DropLinePositionsEnum.NONE);

  // We're adding an attribute directly rather than relying on React to have better performances during drag operations.
  const setDropLinePosition = (newDropLine: DropLinePositionsEnum) => {
    dropLinePosition.current = DropLinePositionsEnum.NONE;
    if (dropLinePreviewRef.current) {
      // We want to avoid drawing both a line below an element and a line above its next sibling
      const itemHasNextSiblings =
        workspaceDragItem && 'isLast' in workspaceDragItem && workspaceDragItem.isLast === false;
      dropLinePreviewRef.current.setAttribute(
        'data-line-position',
        itemHasNextSiblings && newDropLine === DropLinePositionsEnum.BELOW
          ? DropLinePositionsEnum.NONE
          : newDropLine,
      );
    }
  };

  const extraType = options.extra?.type;
  const acceptedTypes = _.compact([extraType, workspaceDragItem?.type]);

  const [collectedProps, dropRef] = useDrop<
    WorkspaceDragItem | DragAndDropItemWithType,
    void,
    WorkspaceDragAndDropCollectedProps
  >({
    accept: acceptedTypes,
    canDrop: (item) => {
      if (item.type === extraType) {
        return Boolean(options.extra?.canDrop && options.extra?.canDrop(item as T));
      }

      return isWorkspaceDragItem(item) && canDropWorkspaceItemAtTarget(item, workspaceDragItem);
    },
    drop: (item, monitor) => {
      // If we can't drop, stop everything.
      if (!monitor.canDrop()) {
        setDropLinePosition(DropLinePositionsEnum.NONE);
        return;
      }

      if (options.extra?.onDrop && item.type === extraType) {
        options.extra.onDrop(item as T);
        setDropLinePosition(DropLinePositionsEnum.NONE);
        return;
      }

      /*
       * If we:
       * - don't have a draggable element
       * - don't have a drop handler
       * - don't have a workspace drag item
       *
       * Then we abort.
       */
      if (!dragDropSourceRef.current || !onWorkspaceItemDrop || !isWorkspaceDragItem(item)) {
        setDropLinePosition(DropLinePositionsEnum.NONE);
        return;
      }

      // Only tags in the personal workspace can be nested using drag and drop.
      const canDropInside =
        item.type === WorkspaceDragSourceTypesEnum.PERSONAL_VIEW &&
        workspaceDragItem?.type === WorkspaceDragSourceTypesEnum.PERSONAL_VIEW &&
        workspaceDragItem?.namespace === item.namespace;

      // We need to re-compute the drop line position because it might not be completely accurate when the drop occurs.
      const newDropLine = computeWorkspaceDragAndDropLinePosition({
        dragClientOffset: monitor.getClientOffset(),
        dropSourceRect: dragDropSourceRef.current.getBoundingClientRect(),
        canDrop: canDropInside,
      });
      onWorkspaceItemDrop(item as WorkspaceDragItem, newDropLine, workspaceDragItem);
      setDropLinePosition(DropLinePositionsEnum.NONE);
    },
    hover: (item, monitor) => {
      // If we can't drop, nothing to do.
      if (!monitor.canDrop()) {
        return;
      }

      const isLineVisible = dropLinePosition.current !== DropLinePositionsEnum.NONE;
      // Make sure we remove the drop line for non-workspace items.
      if (item.type === extraType && isLineVisible) {
        setDropLinePosition(DropLinePositionsEnum.NONE);
      }

      // If we don't have a workspace item, nothing more to do.
      if (!isWorkspaceDragItem(item)) {
        return;
      }

      // If we don't have a draggable element, or no line ref, or we're not over the element, nothing more to do.
      if (!dragDropSourceRef.current || !dropLinePreviewRef.current || !monitor.isOver()) {
        setDropLinePosition(DropLinePositionsEnum.NONE);
        return;
      }

      const type = monitor.getItemType();
      const canDropInside = type === WorkspaceDragSourceTypesEnum.PERSONAL_VIEW;
      const newDropLine = computeWorkspaceDragAndDropLinePosition({
        dragClientOffset: monitor.getClientOffset(),
        dropSourceRect: dragDropSourceRef.current.getBoundingClientRect(),
        canDrop: canDropInside,
      });

      setDropLinePosition(newDropLine);
    },
    collect: (monitor) => {
      const item = monitor.getItem<WorkspaceDragItem | T | null>();
      const type = monitor.getItemType();
      const isDroppingExtra = type === extraType;
      // We don't allow dropping a tag inside another namespace.
      const isDroppingAllowedPersonalCustomView =
        isObject(item) &&
        'namespace' in item &&
        workspaceDragItem?.type === WorkspaceDragSourceTypesEnum.PERSONAL_VIEW &&
        workspaceDragItem?.namespace === item.namespace &&
        workspaceDragItem?.type === type;

      return {
        isOver: monitor.isOver(),
        didDrop: monitor.didDrop(),
        isDropping:
          (isDroppingAllowedPersonalCustomView || isDroppingExtra) && monitor.isOver() && monitor.canDrop(),
        isRefusingDrop: Boolean(type && !monitor.canDrop() && !domNativeTypes.includes(type.toString())),
      };
    },
  });

  // Remove the drop line when the cursor leaves our element.
  useDragLeave(
    () => {
      setDropLinePosition(DropLinePositionsEnum.NONE);
    },
    collectedProps.isOver,
    collectedProps.didDrop,
  );

  const dragSpec = useMemo(
    () => ({
      type: workspaceDragItem?.type || '',
      item: workspaceDragItem,
    }),
    [workspaceDragItem],
  );
  const [, dragRef, dragPreviewRef] = useDrag(dragSpec);

  const dndRef = useCallback(
    (el: HTMLDivElement | null) => {
      dragDropSourceRef.current = el;

      if (!isIntersecting && el) {
        observerRef(el);
      }

      if (el && isIntersecting && !isConnected.current) {
        if (!isDragDisabled) {
          dragRef(dropRef(el));
        } else {
          dropRef(el);
        }

        isConnected.current = true;
      }
    },
    [dragRef, dropRef, isDragDisabled, isIntersecting, observerRef],
  );

  markWorkspaceDragAndDropEnd();
  return {
    /** Ref of the element that needs to be draggable. */
    dndRef,
    /** To be attached on the element used to draw the drag preview. */
    dragPreviewRef,
    /** Ref to attach on the element that will have a drop line during drag operations. */
    dropLinePreviewRef,
    /** Props computed by react-dnd. */
    collectedProps,
  };
}
